// v3
// Path: frontend/src/components/QuickBooksSync/EditConnectionGroupModal.jsx
// Inputs: token, group, dbProfiles, accounts, onClose, onSaved
// Outputs: React JSX modal allowing creation and editing of connection groups with unique IDs and multi-database configurations
// Summary: Connection group editor supporting unique ConnectionGroupID identifiers, custom group names, Network.Interfaces selection, instance credentials, live database selection, and multi-database support per account.

import React, { useState, useEffect } from 'react';

export const EditConnectionGroupModal = ({
    token,
    group,
    dbProfiles = [],
    accounts = [],
    onClose,
    onSaved
}) => {
    const isNew = !group || !group.connectionGroupId;
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    // Group Metadata State
    const [groupName, setGroupName] = useState(group?.groupName || (group?.sourceDatabase ? `Group: ${group.sourceDatabase}` : ''));
    const [connectionGroupId, setConnectionGroupId] = useState(group?.connectionGroupId || '');
    const [sourceSystem, setSourceSystem] = useState(group?.sourceSystem || 'Passport');
    const [targetSystem, setTargetSystem] = useState(group?.targetSystem || 'QuickBooks');

    // Profile & Interface State
    const [selectedProfilePrefix, setSelectedProfilePrefix] = useState(group?.sourceConnPrefix || (dbProfiles[0]?.connPrefix || ''));
    const [availableInterfaces, setAvailableInterfaces] = useState([]);
    const [loadingInterfaces, setLoadingInterfaces] = useState(false);
    const [selectedInterfaceIp, setSelectedInterfaceIp] = useState('');
    const [customIpMode, setCustomIpMode] = useState(false);
    const [customIp, setCustomIp] = useState('');

    // Database State
    const [availableDbs, setAvailableDbs] = useState([]);
    const [loadingDbs, setLoadingDbs] = useState(false);
    const [dbFetchError, setDbFetchError] = useState(null);
    const [selectedDatabase, setSelectedDatabase] = useState(group?.sourceDatabase || 'pbsdata00');
    const [customDbMode, setCustomDbMode] = useState(false);

    // QuickBooks Account State
    const [selectedAccountId, setSelectedAccountId] = useState(String(group?.accountId || (accounts[0]?.AccountID || '')));

    // Credentials & Diagnostics State
    const [showCreds, setShowCreds] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [credUsername, setCredUsername] = useState('sa');
    const [credPassword, setCredPassword] = useState('');
    const [testingConnection, setTestingConnection] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [savingCreds, setSavingCreds] = useState(false);
    const [credSavedNotice, setCredSavedNotice] = useState(false);

    // Parse existing prefix parts: [ServerName|IPAddress|InstanceName|Port|Username]
    const parsePrefix = (prefix) => {
        if (!prefix || !prefix.startsWith('[')) return { server: '', ip: '', inst: '', port: 1433, user: 'sa' };
        const cb = prefix.indexOf(']');
        if (cb === -1) return { server: '', ip: '', inst: '', port: 1433, user: 'sa' };
        const parts = prefix.substring(1, cb).split('|');
        if (parts.length !== 5) return { server: '', ip: '', inst: '', port: 1433, user: 'sa' };
        return {
            server: parts[0],
            ip: parts[1],
            inst: parts[2],
            port: parseInt(parts[3], 10) || 1433,
            user: parts[4]
        };
    };

    const initialParsed = parsePrefix(group.sourceConnPrefix);
    const [port, setPort] = useState(initialParsed.port);

    // Find matched profile in dbProfiles
    const currentMatchedProfile = dbProfiles.find(p => p.connPrefix === selectedProfilePrefix);

    // Build the dynamic connection prefix based on selected interface IP and port
    const getEffectiveConnPrefix = () => {
        const p = parsePrefix(selectedProfilePrefix);
        const effectiveIp = customIpMode ? (customIp.trim() || '127.0.0.1') : (selectedInterfaceIp || p.ip || '127.0.0.1');
        const effectivePort = port || p.port || 1433;
        const effectiveUser = credUsername.trim() || p.user || 'sa';
        return `[${p.server || 'LocalServer'}|${effectiveIp}|${p.inst || 'MSSQLSERVER'}|${effectivePort}|${effectiveUser}]`;
    };

    // Load available network interfaces from Network.Interfaces
    const fetchInterfaces = async (prefix) => {
        if (!prefix) return;
        setLoadingInterfaces(true);
        try {
            const res = await fetch(`/api/quickbooks/instance-interfaces?connPrefix=${encodeURIComponent(prefix)}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success && Array.isArray(data.interfaces)) {
                setAvailableInterfaces(data.interfaces);
                // Match the IP from the selected prefix
                const targetIp = parsePrefix(prefix).ip;
                const match = data.interfaces.find(iface => iface.IPAddress === targetIp);
                if (match) {
                    setSelectedInterfaceIp(match.IPAddress);
                    setCustomIpMode(false);
                } else if (data.interfaces.length > 0) {
                    setSelectedInterfaceIp(data.interfaces[0].IPAddress);
                    setCustomIpMode(false);
                } else if (targetIp) {
                    setSelectedInterfaceIp(targetIp);
                }
            } else {
                setAvailableInterfaces([]);
            }
        } catch (err) {
            console.warn('[EditConnectionGroupModal] Error fetching interfaces:', err);
            setAvailableInterfaces([]);
        } finally {
            setLoadingInterfaces(false);
        }
    };

    // Load credentials from Vault.Credentials
    const fetchCredentials = async (prefix) => {
        if (!prefix) return;
        try {
            const res = await fetch(`/api/quickbooks/instance-credential?connPrefix=${encodeURIComponent(prefix)}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                if (data.username) setCredUsername(data.username);
                if (data.password) setCredPassword(data.password);
                if (data.port) setPort(data.port);
            }
        } catch (err) {
            console.warn('[EditConnectionGroupModal] Error fetching credentials:', err);
        }
    };

    // Load live databases for the profile
    const fetchDatabases = async (prefixToUse) => {
        if (!prefixToUse) return;
        setLoadingDbs(true);
        setDbFetchError(null);
        try {
            const res = await fetch(`/api/quickbooks/databases?connPrefix=${encodeURIComponent(prefixToUse)}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success && Array.isArray(data.databases)) {
                setAvailableDbs(data.databases);
                setDbFetchError(null);
            } else if (data.error) {
                setAvailableDbs([]);
                setDbFetchError(data.error);
                setShowCreds(true);
            } else {
                setAvailableDbs([]);
            }
        } catch (err) {
            setAvailableDbs([]);
            setDbFetchError(err.message);
            setShowCreds(true);
        } finally {
            setLoadingDbs(false);
        }
    };

    // Initial load
    useEffect(() => {
        if (group.sourceConnPrefix) {
            fetchInterfaces(group.sourceConnPrefix);
            fetchCredentials(group.sourceConnPrefix);
            fetchDatabases(group.sourceConnPrefix);
        }
    }, [group.sourceConnPrefix]);

    // Handle user changing Database Profile
    const handleProfileChange = (newPrefix) => {
        setSelectedProfilePrefix(newPrefix);
        const parsed = parsePrefix(newPrefix);
        setPort(parsed.port);
        setSelectedInterfaceIp(parsed.ip);
        setCustomIpMode(false);
        setTestResult(null);
        fetchInterfaces(newPrefix);
        fetchCredentials(newPrefix);
        fetchDatabases(newPrefix);
    };

    // Handle user changing Network Interface
    const handleInterfaceChange = (ip) => {
        if (ip === '__CUSTOM__') {
            setCustomIpMode(true);
        } else {
            setCustomIpMode(false);
            setSelectedInterfaceIp(ip);
            // Refresh databases with this specific interface IP
            const p = parsePrefix(selectedProfilePrefix);
            const testPrefix = `[${p.server}|${ip}|${p.inst}|${port}|${credUsername}]`;
            fetchDatabases(testPrefix);
        }
    };

    // Handle user changing Port
    const handlePortChange = (newPort) => {
        setPort(newPort);
        const p = parsePrefix(selectedProfilePrefix);
        const effectiveIp = customIpMode ? (customIp.trim() || '127.0.0.1') : (selectedInterfaceIp || p.ip || '127.0.0.1');
        const testPrefix = `[${p.server}|${effectiveIp}|${p.inst}|${newPort}|${credUsername}]`;
        fetchDatabases(testPrefix);
    };

    // Test connection
    const handleTestConnection = async () => {
        setTestingConnection(true);
        setTestResult(null);
        try {
            const p = parsePrefix(selectedProfilePrefix);
            const effectiveIp = customIpMode ? (customIp.trim() || '127.0.0.1') : (selectedInterfaceIp || p.ip || '127.0.0.1');
            const effectivePort = port || p.port || 1433;
            const effectiveUser = credUsername.trim() || p.user || 'sa';
            const effectivePrefix = `[${p.server || 'LocalServer'}|${effectiveIp}|${p.inst || 'MSSQLSERVER'}|${effectivePort}|${effectiveUser}]`;

            const res = await fetch('/api/quickbooks/test-instance-connection', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    connPrefix: effectivePrefix,
                    username: effectiveUser,
                    password: credPassword,
                    ipAddress: effectiveIp,
                    port: effectivePort
                })
            });
            const data = await res.json();
            setTestResult(data);
            if (data.success && Array.isArray(data.databases)) {
                setAvailableDbs(data.databases);
                setDbFetchError(null);
            }
        } catch (err) {
            setTestResult({ success: false, error: err.message });
        } finally {
            setTestingConnection(false);
        }
    };

    // Save credentials to Vault.Credentials
    const handleSaveCredentials = async () => {
        setSavingCreds(true);
        setCredSavedNotice(false);
        try {
            const effectivePrefix = getEffectiveConnPrefix();
            const res = await fetch('/api/quickbooks/instance-credential', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    connPrefix: effectivePrefix,
                    username: credUsername,
                    password: credPassword
                })
            });
            const data = await res.json();
            if (data.success) {
                setCredSavedNotice(true);
                setTimeout(() => setCredSavedNotice(false), 3500);
                fetchDatabases(effectivePrefix);
            } else {
                alert(`Error saving credentials: ${data.error}`);
            }
        } catch (err) {
            alert(`Error saving credentials: ${err.message}`);
        } finally {
            setSavingCreds(false);
        }
    };

    // Save whole connection group
    const handleUpdateGroup = async (e) => {
        e.preventDefault();
        if (!selectedDatabase) {
            alert('Please select or specify a source database name.');
            return;
        }
        if (!selectedAccountId) {
            alert('Please select a target QuickBooks account.');
            return;
        }

        const effectivePrefix = getEffectiveConnPrefix();

        setSaving(true);
        try {
            let res;
            if (isNew) {
                res = await fetch('/api/quickbooks/connection-groups', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        connectionGroupId: connectionGroupId.trim() || undefined,
                        groupName: groupName.trim() || `Group: ${selectedDatabase}`,
                        accountId: selectedAccountId,
                        sourceConnPrefix: effectivePrefix,
                        sourceDatabaseName: selectedDatabase,
                        sourceSystem,
                        targetSystem,
                        createDefaultPipelines: true
                    })
                });
            } else {
                res = await fetch('/api/quickbooks/connection-group/update', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        connectionGroupId: group?.connectionGroupId,
                        groupName: groupName.trim() || undefined,
                        oldConnPrefix: group?.sourceConnPrefix,
                        oldDatabaseName: group?.sourceDatabase,
                        oldAccountId: group?.accountId,
                        newConnPrefix: effectivePrefix,
                        newDatabaseName: selectedDatabase,
                        newAccountId: selectedAccountId
                    })
                });
            }

            const data = await res.json();
            if (!res.ok) {
                throw new Error(data.error || 'Failed to save connection group.');
            }

            if (onSaved) onSaved();
            onClose();
        } catch (err) {
            alert(`Error saving connection group: ${err.message}`);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-3 sm:p-6 overflow-y-auto">
            <div className="bg-slate-850 border border-slate-700 rounded-2xl max-w-2xl w-full p-6 shadow-2xl space-y-4 my-8">
                
                {/* Header */}
                <div className="flex justify-between items-center border-b border-slate-700 pb-3">
                    <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-lg bg-blue-600/20 border border-blue-500/40 flex items-center justify-center text-blue-400">
                            <span className="material-symbols-outlined text-[18px]">settings_ethernet</span>
                        </div>
                        <div>
                            <h3 className="text-base font-bold text-white">
                                {isNew ? 'New Connection Group' : 'Edit Connection Group Settings'}
                            </h3>
                            <p className="text-xs text-slate-400">
                                {isNew 
                                    ? 'Creates an independent connection group with a unique identifier, allowing multiple database integrations per customer.'
                                    : 'Updates the shared SQL Server instance, network interface, database, and QuickBooks account for all pipelines in this set.'}
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-white">
                        <span className="material-symbols-outlined">close</span>
                    </button>
                </div>

                {/* Member Pipelines Badge (if existing) */}
                {group?.pipelines && group.pipelines.length > 0 && (
                    <div className="p-3 bg-slate-900 border border-slate-750 rounded-xl space-y-1.5 text-xs">
                        <div className="text-slate-400 font-semibold uppercase tracking-wider text-[11px] flex items-center gap-1.5">
                            <span className="material-symbols-outlined text-[15px] text-blue-400">link</span>
                            <span>{group.pipelines.length} Member Pipelines in this Group (ID: <span className="font-mono text-sky-300">{group.connectionGroupId || 'Auto'}</span>):</span>
                        </div>
                        <div className="flex items-center gap-1.5 flex-wrap">
                            {group.pipelines.map(p => (
                                <span key={p.ConfigID} className="bg-slate-950 px-2 py-0.5 rounded border border-slate-700 text-sky-300 font-mono text-[11px]">
                                    {p.EntityType} ({p.TableName || 'Auto'})
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                <form onSubmit={handleUpdateGroup} className="space-y-4">
                    {/* Unique Identifier & Group Name */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3 bg-slate-900/60 border border-slate-750 rounded-xl">
                        <div>
                            <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1">
                                Unique Group Identifier
                            </label>
                            <input
                                type="text"
                                value={connectionGroupId}
                                onChange={e => setConnectionGroupId(e.target.value)}
                                disabled={!isNew}
                                placeholder={isNew ? 'Auto-generated (e.g. CG-1-PBS00)' : ''}
                                className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white font-mono focus:outline-none focus:border-blue-500 disabled:opacity-75"
                            />
                            <p className="text-[10px] text-slate-400 mt-1">Unique key distinguishing this database connection suite.</p>
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1">
                                Group Name *
                            </label>
                            <input
                                type="text"
                                value={groupName}
                                onChange={e => setGroupName(e.target.value)}
                                required
                                placeholder="e.g. Production Warehouse PBS00"
                                className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500"
                            />
                            <p className="text-[10px] text-slate-400 mt-1">Descriptive label for customer dashboard.</p>
                        </div>
                    </div>
                    
                    {/* Database Profile (SQL Server Instance) */}
                    <div>
                        <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1">
                            SQL Server Instance / Database Profile *
                        </label>
                        <select
                            value={selectedProfilePrefix}
                            onChange={(e) => handleProfileChange(e.target.value)}
                            required
                            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500"
                        >
                            {dbProfiles.map(p => (
                                <option key={p.connPrefix} value={p.connPrefix}>
                                    {p.displayName}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Network Interface Selection (Network.Interfaces) */}
                    <div className="p-3 bg-slate-900 border border-slate-750 rounded-xl space-y-2">
                        <div className="flex justify-between items-center">
                            <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider flex items-center gap-1.5">
                                <span className="material-symbols-outlined text-emerald-400 text-[16px]">lan</span>
                                <span>Network Interface / Connection IP (Network.Interfaces) *</span>
                            </label>
                            <span className="text-[11px] text-slate-400">
                                {loadingInterfaces ? 'Loading interfaces...' : `${availableInterfaces.length} registered interfaces`}
                            </span>
                        </div>

                        {customIpMode ? (
                            <div className="space-y-1">
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        placeholder="e.g. 10.9.8.1 or localhost"
                                        value={customIp}
                                        onChange={(e) => setCustomIp(e.target.value)}
                                        className="flex-1 bg-slate-950 border border-slate-700 rounded px-2.5 py-1.5 text-xs text-white font-mono"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setCustomIpMode(false)}
                                        className="px-2.5 py-1 bg-slate-800 hover:bg-slate-750 text-slate-300 rounded text-xs"
                                    >
                                        Use Interface List
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-1">
                                <select
                                    value={selectedInterfaceIp}
                                    onChange={(e) => handleInterfaceChange(e.target.value)}
                                    className="w-full bg-slate-950 border border-slate-700 rounded px-2.5 py-1.5 text-xs text-sky-300 font-mono font-bold focus:outline-none focus:border-blue-500"
                                >
                                    {availableInterfaces.map(iface => (
                                        <option key={iface.InterfaceID} value={iface.IPAddress}>
                                            {iface.IPAddress} &mdash; [{iface.IPType || 'LAN'}] {iface.VpnRole ? `(${iface.VpnRole})` : ''} {iface.IsOnline ? '🟢 Online' : '⚪ Offline'} {iface.Notes ? ` - ${iface.Notes}` : ''}
                                        </option>
                                    ))}
                                    <option value="__CUSTOM__">➕ Enter Custom IP / Hostname...</option>
                                </select>
                                <p className="text-[10px] text-slate-400">
                                    Determines the exact network interface IP used by the SQL client to reach this instance.
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Instance Port */}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1">
                                SQL Server Port
                            </label>
                            <input
                                type="number"
                                value={port}
                                onChange={(e) => handlePortChange(parseInt(e.target.value, 10) || 1433)}
                                className="w-full bg-slate-900 border border-slate-700 rounded px-2.5 py-1.5 text-xs text-white font-mono"
                            />
                        </div>

                        <div>
                            <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1">
                                Target QuickBooks Account *
                            </label>
                            <select
                                value={selectedAccountId}
                                onChange={(e) => setSelectedAccountId(e.target.value)}
                                required
                                className="w-full bg-slate-900 border border-slate-700 rounded px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-blue-500"
                            >
                                {accounts.map(acc => (
                                    <option key={acc.AccountID} value={acc.AccountID}>
                                        {acc.AccountName} ({acc.Environment === 'sandbox' ? 'Sandbox' : 'Production'})
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Credentials & Connectivity Diagnostics Panel */}
                    <div className="bg-slate-900 border border-slate-750 rounded-xl p-3 space-y-2 text-xs">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1.5 font-semibold text-slate-200">
                                <span className="material-symbols-outlined text-[16px] text-amber-400">key</span>
                                <span>SQL Instance Credentials (Vault.Credentials)</span>
                            </div>
                            <button
                                type="button"
                                onClick={() => setShowCreds(!showCreds)}
                                className="text-sky-400 hover:text-sky-300 text-[11px] underline"
                            >
                                {showCreds ? 'Hide Details' : 'Inspect / Edit Credentials'}
                            </button>
                        </div>

                        {/* Status Message */}
                        {loadingDbs ? (
                            <div className="flex items-center gap-1.5 text-slate-400">
                                <div className="w-3 h-3 border-2 border-sky-400 border-t-transparent rounded-full animate-spin"></div>
                                <span>Connecting to instance...</span>
                            </div>
                        ) : dbFetchError ? (
                            <div className="p-2 bg-rose-950/60 border border-rose-800 rounded-lg text-rose-200 space-y-1">
                                <div className="font-bold">⚠️ Connection Issue:</div>
                                <p className="font-mono text-[11px] break-all">{dbFetchError}</p>
                            </div>
                        ) : availableDbs.length > 0 ? (
                            <div className="text-emerald-400 text-[11px]">
                                🟢 Connected ({availableDbs.length} online databases available)
                            </div>
                        ) : null}

                        {/* Expandable Credentials Drawer */}
                        {showCreds && (
                            <div className="pt-2 border-t border-slate-800 space-y-2">
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <label className="block text-[10px] text-slate-400 uppercase font-semibold mb-0.5">
                                            Login Username
                                        </label>
                                        <input
                                            type="text"
                                            value={credUsername}
                                            onChange={(e) => setCredUsername(e.target.value)}
                                            className="w-full bg-slate-950 border border-slate-700 rounded px-2.5 py-1.5 text-xs text-white font-mono"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[10px] text-slate-400 uppercase font-semibold mb-0.5">
                                            Login Password
                                        </label>
                                        <div className="relative">
                                            <input
                                                type={showPassword ? 'text' : 'password'}
                                                value={credPassword}
                                                onChange={(e) => setCredPassword(e.target.value)}
                                                placeholder="Enter password..."
                                                className="w-full bg-slate-950 border border-slate-700 rounded px-2.5 py-1.5 text-xs text-white font-mono pr-7"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="absolute right-2 top-2 text-slate-400 hover:text-slate-200 text-xs"
                                            >
                                                {showPassword ? '🙈' : '👁️'}
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-center justify-between pt-1">
                                    <div className="flex items-center gap-2">
                                        <button
                                            type="button"
                                            disabled={testingConnection}
                                            onClick={handleTestConnection}
                                            className="px-2.5 py-1 bg-slate-800 hover:bg-slate-750 text-slate-200 border border-slate-650 rounded text-xs flex items-center gap-1.5 transition-colors disabled:opacity-50"
                                        >
                                            {testingConnection && <div className="w-2.5 h-2.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
                                            <span>Test Connection</span>
                                        </button>

                                        <button
                                            type="button"
                                            disabled={savingCreds}
                                            onClick={handleSaveCredentials}
                                            className="px-2.5 py-1 bg-amber-600 hover:bg-amber-500 text-white rounded text-xs font-semibold flex items-center gap-1.5 transition-colors disabled:opacity-50"
                                        >
                                            {savingCreds && <div className="w-2.5 h-2.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
                                            <span>Save to Vault</span>
                                        </button>
                                    </div>

                                    {credSavedNotice && (
                                        <span className="text-emerald-400 font-semibold text-[11px] animate-pulse">
                                            ✓ Saved to Vault.Credentials!
                                        </span>
                                    )}
                                </div>

                                {/* Test Result Message */}
                                {testResult && (
                                    <div className={`p-2 rounded text-[11px] font-mono ${testResult.success ? 'bg-emerald-950/60 border border-emerald-800 text-emerald-300' : 'bg-rose-950/60 border border-rose-800 text-rose-300'}`}>
                                        {testResult.success ? (
                                            <div>
                                                <div className="font-bold">✓ {testResult.message}</div>
                                                <div className="text-[10px] text-emerald-400/80 mt-0.5">
                                                    Databases: {testResult.databases?.join(', ')}
                                                </div>
                                            </div>
                                        ) : (
                                            <div>
                                                <div className="font-bold">✗ Connection Test Failed:</div>
                                                <div className="text-[10px] mt-0.5">{testResult.error}</div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Source Database Selection */}
                    <div>
                        <div className="flex justify-between items-center mb-1">
                            <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider">
                                Source Database Name *
                            </label>
                            <button
                                type="button"
                                onClick={() => setCustomDbMode(!customDbMode)}
                                className="text-[11px] text-blue-400 hover:text-blue-300 underline"
                            >
                                {customDbMode ? 'Select from list' : 'Type custom DB'}
                            </button>
                        </div>

                        {customDbMode ? (
                            <input
                                type="text"
                                placeholder="e.g. pbsdata00"
                                value={selectedDatabase}
                                onChange={(e) => setSelectedDatabase(e.target.value)}
                                required
                                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500 font-mono"
                            />
                        ) : (
                            <select
                                value={selectedDatabase}
                                onChange={(e) => setSelectedDatabase(e.target.value)}
                                required
                                disabled={loadingDbs || availableDbs.length === 0}
                                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500 font-mono disabled:opacity-50"
                            >
                                <option value="">
                                    {loadingDbs ? 'Loading live databases...' : (availableDbs.length === 0 ? '-- No databases loaded (check credentials) --' : '-- Select Database --')}
                                </option>
                                {availableDbs.map(db => (
                                    <option key={db} value={db}>{db}</option>
                                ))}
                            </select>
                        )}
                    </div>

                    {/* Footer Buttons */}
                    <div className="flex justify-end gap-3 pt-4 border-t border-slate-700">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 rounded-lg text-sm text-slate-300 hover:text-white bg-slate-800 hover:bg-slate-700 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={saving}
                            className="px-5 py-2 rounded-lg text-sm font-semibold text-white bg-blue-600 hover:bg-blue-500 transition-colors shadow-lg shadow-blue-500/25 disabled:opacity-50 flex items-center gap-2"
                        >
                            {saving && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
                            <span>{isNew ? 'Create Connection Group' : `Apply to All ${group?.pipelines?.length || 0} Pipelines`}</span>
                        </button>
                    </div>

                </form>

            </div>
        </div>
    );
};

export default EditConnectionGroupModal;
